import axios from 'axios';
import { getBongoUrl } from '../common/utils/bongo';

export type BusyRecordStatus = 'pending' | 'validating' | 'needs_attention';

/** A mapping record the new editor has an edit in progress on. */
export type BusyRecord = {
  id: string;
  name: string;
  status: BusyRecordStatus;
};

export type BusyRecordsType = 'conversions' | 'audiences';

/**
 * Records of one type the new editor is working on (RGI-2618): pending,
 * validating or needs_attention. While any exists, a legacy publish of the
 * same item is overwritten by the mirror as soon as that record promotes —
 * days later for a parked candidate — so the legacy pages lock Publish and
 * Delete and say why.
 *
 * Fail-open: the read only drives the banner; model-metadata refuses the
 * write with a 409 on the same condition, whatever this returned.
 */
export async function fetchBusyRecords(
  tenant: number,
  type: BusyRecordsType
): Promise<BusyRecord[]> {
  try {
    const { data } = await axios.get(
      `${getBongoUrl()}/v1/org/${tenant}/data/mappings/busy-records/${type}`
    );
    return Array.isArray(data?.records) ? data.records : [];
  } catch {
    return [];
  }
}

/** The busy record named like `name`, if any — case-insensitive, the way
 * model-metadata matches audiences. */
export function findBusyRecord(
  records: BusyRecord[],
  name: string | null | undefined
): BusyRecord | undefined {
  const wanted = name?.trim().toLowerCase();
  if (!wanted) return undefined;
  return records.find((record) => record.name.trim().toLowerCase() === wanted);
}

/** The banner text: names the blocker(s) and where to resolve them.
 *
 * Conversions are saved together, so one busy record locks the whole page.
 * Audiences are one yaml each and only a same-named audience is locked
 * (RGI-2956), so their wording says the rest can still be published. */
export function busyRecordsMessage(
  records: BusyRecord[],
  type: BusyRecordsType = 'conversions'
): string {
  if (type === 'audiences') {
    const names = records.map((record) => `"${record.name.trim()}"`).join(', ');
    return records.length === 1
      ? `${names} has unfinished changes in the new editor, so it can't be published here until they're finished or discarded. You can still publish other audiences.`
      : `${names} have unfinished changes in the new editor, so they can't be published here until those are finished or discarded. You can still publish other audiences.`;
  }

  const names = records.map((record) => record.name).join(', ');
  return records.length === 1
    ? `${names} has an edit in progress in the new editor. Finish or discard it there to publish here.`
    : `${names} have edits in progress in the new editor. Finish or discard them there to publish here.`;
}

/** The message a 409 from bongo/model-metadata carries, else the generic one. */
export function requestErrorMessage(err: any): string {
  return err?.response?.data?.message ?? err?.message ?? 'Request failed';
}
